// Re-import from Scrivener (phase 3 of the browser import; File ▸ Import ▸ Scrivener Project… on a Mac for a project
// already in the library): the open project's package, zipped or as its folder, merged three-way through the live
// project — Scrivener's changes since the last import taken, the writer's own kept, both-changed text offered as a
// suggestion — then the report. A package that looks like an older copy is refused with the reason and Import Anyway.
import { errorText } from '@writing-suite/client/browser/usage.ts';
import { ScrivenerError, type ScrivenerImportReport, type ScrivenerPackage } from '@writing-suite/core/import';
import { useRef, useState } from 'react';
import { announce, useApp } from '../../shell/context.ts';
import { historyText, importHistory, packageFromFiles, packageFromZip, reimportPackage, reportLines, uploadAttachments } from '../../store/importScrivener.ts';
import type { LiveProject } from '../../store/project.ts';
import { DialogShell } from './BeatBoard.tsx';

/** The two pickers for a Scrivener package: a zip, or the `.scriv` folder itself. */
export function ScrivenerPickers({ busy, onPackage, onError }: { busy: boolean; onPackage: (pkg: ScrivenerPackage) => void; onError: (message: string) => void }) {
  const zipInput = useRef<HTMLInputElement>(null);
  const folderInput = useRef<HTMLInputElement>(null);
  const read = async (fn: () => Promise<ScrivenerPackage>) => {
    try {
      onPackage(await fn());
    } catch (e) {
      onError(e instanceof Error ? e.message : String(e));
    }
  };
  return (
    <>
      <p className="scrivener-pickers">
        <button type="button" className="button secondary" onClick={() => zipInput.current?.click()} disabled={busy}>
          Choose a Zipped Project…
        </button>
        <button type="button" className="button secondary" onClick={() => folderInput.current?.click()} disabled={busy}>
          Choose a .scriv Folder…
        </button>
      </p>
      <input
        ref={zipInput}
        type="file"
        accept=".zip,application/zip"
        hidden
        data-scrivener-zip
        onChange={(e) => {
          const file = e.target.files?.[0];
          e.target.value = '';
          if (file) void read(async () => packageFromZip(new Uint8Array(await file.arrayBuffer())));
        }}
      />
      <input
        ref={folderInput}
        type="file"
        hidden
        data-scrivener-folder
        // @ts-expect-error webkitdirectory is a non-standard attribute every browser honours.
        webkitdirectory=""
        onChange={(e) => {
          const files = e.target.files;
          if (files && files.length) void read(() => packageFromFiles(files));
          e.target.value = '';
        }}
      />
    </>
  );
}

/** The import report: the counts, then anything to know, folded. */
export function ImportReportView({ report }: { report: ScrivenerImportReport }) {
  const issues = report.issues;
  return (
    <div className="import-report" data-import-report>
      <p className="import-report-title">{report.projectTitle}</p>
      <ul>
        {reportLines(report).map((line) => (
          <li key={line}>{line}</li>
        ))}
      </ul>
      {issues.length ? (
        <details>
          <summary>{issues.length === 1 ? '1 thing to know' : `${issues.length} things to know`}</summary>
          <ul className="import-issues">
            {issues.slice(0, 60).map((i, k) => (
              <li key={k}>
                <span className="import-issue-item">{i.item}</span> {i.message}
              </li>
            ))}
            {issues.length > 60 ? <li>…and {issues.length - 60} more</li> : null}
          </ul>
        </details>
      ) : null}
    </div>
  );
}

export function ScrivenerReimportDialog({ live, projectId, projectKey, author, onClose }: {
  live: LiveProject; projectId: string; projectKey: Uint8Array | null; author: string; onClose: () => void;
}) {
  const app = useApp();
  const [pkg, setPkg] = useState<ScrivenerPackage | null>(null);
  const [busy, setBusy] = useState(false);
  const [report, setReport] = useState<ScrivenerImportReport | null>(null);
  const [older, setOlder] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Only the writing history: nothing else is read, so it works where importing again can't (a project restructured
  // here past Scrivener's, or an older copy).
  const [historyOnly, setHistoryOnly] = useState(false);
  const [historyDone, setHistoryDone] = useState<string | null>(null);

  const run = async (allowOlder: boolean) => {
    if (!pkg) return;
    setBusy(true);
    setError(null);
    setOlder(null);
    if (historyOnly) {
      try {
        const summary = importHistory(live, pkg);
        const text = historyText(summary);
        setHistoryDone(summary.changed === 0 ? `Scrivener’s writing history was already here: ${text}.` : `Added Scrivener’s writing history: ${text}.`);
        announce('Imported Scrivener’s writing history');
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e));
      } finally {
        setBusy(false);
      }
      return;
    }
    // A frame, so the busy state paints before the merge runs in one go.
    await new Promise((resolve) => setTimeout(resolve, 0));
    try {
      const result = reimportPackage(live, pkg, { author, allowOlder });
      if (result.attachments.length && projectKey) {
        try {
          await uploadAttachments(app.account.session.api, projectId, projectKey, result.attachments, live.mergedCursor);
        } catch (e) {
          result.report.issues.push({ item: 'Attachments', message: `Couldn’t upload Scrivener’s files: ${errorText(e, 'projects')}` });
        }
      }
      announce(`Re-imported “${result.report.projectTitle}”`);
      setReport(result.report);
    } catch (e) {
      if (e instanceof ScrivenerError && e.kind === 'olderCopy') setOlder(e.message);
      else setError(e instanceof ScrivenerError ? e.message : errorText(e, 'projects'));
    } finally {
      setBusy(false);
    }
  };

  if (historyDone) {
    return (
      <DialogShell title="Writing History Imported" onCancel={onClose} className="new-project">
        <p className="scrivener-note" data-history-imported>{historyDone}</p>
        <div className="actions">
          <button type="button" className="button primary" onClick={onClose} data-reimport-done>
            Done
          </button>
        </div>
      </DialogShell>
    );
  }
  if (report) {
    return (
      <DialogShell title="Re-imported from Scrivener" onCancel={onClose} className="new-project">
        <ImportReportView report={report} />
        <div className="actions">
          <button type="button" className="button primary" onClick={onClose} data-reimport-done>
            Done
          </button>
        </div>
      </DialogShell>
    );
  }
  return (
    <DialogShell title="Import from Scrivener Again" onCancel={onClose} className="new-project">
      <p className="scrivener-note">
        Choose the Scrivener project this one was imported from. What changed in Scrivener since the last import is brought in; what you changed here stays.
        Where both changed the same text, yours is kept and Scrivener’s is offered as a suggestion.
      </p>
      <div className="scrivener-import" data-scrivener-import>
        {pkg ? (
          <p className="scrivener-chosen">
            <strong>{pkg.title}</strong> · {pkg.allItems().length} items
            <button type="button" className="link-button" onClick={() => { setPkg(null); setOlder(null); }} disabled={busy}>
              Choose another
            </button>
          </p>
        ) : (
          <ScrivenerPickers busy={busy} onPackage={(p) => { setPkg(p); setError(null); setOlder(null); }} onError={setError} />
        )}
      </div>
      <fieldset className="stage-format" data-import-choice={historyOnly ? 'history' : 'project'}>
        <legend className="field-label">Import</legend>
        <label>
          <input type="radio" name="scrivener-import" value="project" checked={!historyOnly} onChange={() => { setHistoryOnly(false); setError(null); }} disabled={busy} />
          Changes since the last import
        </label>
        <label>
          <input type="radio" name="scrivener-import" value="history" checked={historyOnly} onChange={() => { setHistoryOnly(true); setError(null); setOlder(null); }} disabled={busy} />
          Only the writing history{' '}
          <span className="template-summary">The days in Scrivener’s Project ▸ Writing History, for the activity grid and streaks. Nothing else changes.</span>
        </label>
      </fieldset>
      {older ? (
        <p className="error" role="alert" data-older-copy>
          {older}
        </p>
      ) : null}
      {error ? (
        <p className="error" role="alert">
          {error}
        </p>
      ) : null}
      <div className="actions">
        <button type="button" className="button secondary" onClick={onClose} disabled={busy}>
          Cancel
        </button>
        {older ? (
          <button type="button" className="button secondary" onClick={() => void run(true)} disabled={busy} data-import-anyway>
            Import Anyway
          </button>
        ) : null}
        <button type="button" className="button primary" onClick={() => void run(false)} disabled={busy || !pkg} data-reimport>
          {busy ? 'Importing…' : 'Import'}
        </button>
      </div>
    </DialogShell>
  );
}

/** Statistics ▸ Import Scrivener History… (the Mac's Writing activity card): only the package's writing history — a
 *  zipped project, one of Scrivener's backups, or the `.scriv` folder — into the open project. */
export function ScrivenerHistoryDialog({ live, onClose }: { live: LiveProject; onClose: () => void }) {
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);
  const take = (pkg: ScrivenerPackage) => {
    setError(null);
    try {
      const summary = importHistory(live, pkg);
      const text = historyText(summary);
      setDone(summary.changed === 0 ? `Scrivener’s writing history was already here: ${text}.` : `Added Scrivener’s writing history: ${text}.`);
      announce('Imported Scrivener’s writing history');
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  };
  return (
    <DialogShell title="Import Scrivener History" onCancel={onClose} className="new-project">
      {done ? (
        <p className="scrivener-note" data-history-imported>{done}</p>
      ) : (
        <>
          <p className="scrivener-note">
            Choose the Scrivener project, or one of its backups. The days in its Project ▸ Writing History are added to the activity grid and streaks;
            nothing else in the project changes. Scrivener keeps one net figure a day, so a gain counts as written and a loss as cut.
          </p>
          <div className="scrivener-import" data-scrivener-history>
            <ScrivenerPickers busy={false} onPackage={take} onError={setError} />
          </div>
        </>
      )}
      {error ? (
        <p className="error" role="alert">
          {error}
        </p>
      ) : null}
      <div className="actions">
        <button type="button" className={done ? 'button primary' : 'button secondary'} onClick={onClose} data-history-done>
          {done ? 'Done' : 'Cancel'}
        </button>
      </div>
    </DialogShell>
  );
}
